import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { useAuth } from "../../lib/auth.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { Spinner } from "../../components/ui.js";
import { MotdDocument } from "./MotdDocument.js";
import "./motd-prose.css";
import { motdChannelForEnv } from "./channel.js";
import type { MotdChannel } from "./types.js";
import { useMotdCurrent } from "./useMotd.js";

const LINGER_MS = 450;
const LEAVE_GRACE_MS = 150;

/**
 * Logged-in header MOTD control: linger hovercard preview; click opens archive.
 * Staging defaults to staging channel; Admin can also view Dev MOTD.
 */
export function MotdNavButton(): JSX.Element | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { deployEnv, isLoading: featuresLoading } = useFeatures();
  const envChannel = motdChannelForEnv(deployEnv);
  const isAdmin = Boolean(user?.roles.includes("Admin"));
  const canPickDev = isAdmin && envChannel === "staging";

  const [selected, setSelected] = useState<MotdChannel | null>(null);
  const channel = selected ?? envChannel;

  useEffect(() => {
    setSelected(null);
  }, [envChannel]);

  const { data, isLoading, isError } = useMotdCurrent(channel);
  const [open, setOpen] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = (): void => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => () => clearTimers(), []);

  if (featuresLoading || envChannel == null || channel == null) return null;

  const archiveTo = `/motd?channel=${channel}${data?.id ? `&id=${data.id}` : ""}`;

  const onEnter = (): void => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open) return;
    openTimer.current = setTimeout(() => setOpen(true), LINGER_MS);
  };

  const onLeave = (): void => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    closeTimer.current = setTimeout(() => setOpen(false), LEAVE_GRACE_MS);
  };

  return (
    <span className="relative inline-block" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Message of the day"
        title="Message of the day"
        onClick={() => {
          clearTimers();
          setOpen(false);
          void navigate(archiveTo);
        }}
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm font-medium",
          open
            ? "border-brand/40 bg-brand/10 text-brand"
            : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50",
        )}
      >
        MOTD
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Message of the day preview"
          className="absolute right-0 top-[calc(100%+6px)] z-40 w-[min(36rem,calc(100vw-2rem))] rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {canPickDev ? (
            <div className="mb-2 flex items-center gap-1 rounded-md bg-slate-50 p-1">
              {(
                [
                  ["staging", "Staging"],
                  ["dev", "Dev"],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={clsx(
                    "flex-1 rounded px-2 py-1 text-xs font-medium",
                    channel === value
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-600 hover:text-slate-900",
                  )}
                  onClick={() => setSelected(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          ) : null}

          {isLoading ? (
            <div className="px-3 py-6">
              <Spinner label="Loading MOTD…" />
            </div>
          ) : isError || !data ? (
            <p className="px-3 py-4 text-sm text-slate-500">No message available.</p>
          ) : (
            <MotdDocument
              entry={data}
              className="shadow-none"
              bodyClassName="max-h-[min(50vh,22rem)] overflow-y-auto"
              footer={
                <div className="flex items-center justify-between gap-3">
                  <span className="tabular-nums">{data.date}</span>
                  <Link
                    to={archiveTo}
                    className="font-medium text-[#0b5cab] underline-offset-2 hover:underline"
                    onClick={() => setOpen(false)}
                  >
                    Past Message Archive
                  </Link>
                </div>
              }
            />
          )}
        </div>
      ) : null}
    </span>
  );
}
